/**
 * Config Date should handle all helpers related to
 * Delphi dates configuration
 */
import { getCurrencySign } from '@/utils/currency'
import { addDays, addMonths, max } from 'date-fns'
import { useI18n, type ComposerTranslation } from 'vue-i18n'

/**
 * getMinDate will calculate the minDate based on
 * possible backoffice options
 *
 * In the backoffice:
 * - user can choose a specific min date (for example 15th of April)
 * - user can choose a delay before being able to book (example 4 days)
 *
 * Based on provided options and the current date, it will return the minDate
 */
export function getMinDate(
  options: {
    minDate?: Date
    delayDays?: number
  } = {},
): Date {
  const today = new Date()

  // We calculate the possible date for each option
  const minDate = options.minDate || today
  const delayedDate = options.delayDays ? addDays(today, options.delayDays) : today

  // We take the latest of the possible options
  return max([today, minDate, delayedDate])
}

/**
 * getMaxDate will calculate the max date based on
 * possible backoffice options
 *
 * In the backoffice:
 * - user can choose a max delay to book (example 3 months)
 *
 * Based on provided options and the current date, it will return the correct maxDate
 * or undefined if there is no specific max date
 */
export function getMaxDate({
  delayMonths,
}: {
  delayMonths?: number
} = {}): Date | undefined {
  if (delayMonths) {
    const today = new Date()
    return addMonths(today, delayMonths)
  }

  return undefined
}

/**
 * Will return the latest hour time between the parameters
 * @param first contains the hour time such as: "HH:MM:SS"
 * @param second contains the hour time such as: "HH:MM:SS"
 * @returns either first or second parameter
 */
export function maxTime(first: string, second: string) {
  if (first === '') {
    return second
  }
  if (second === '') {
    return first
  }
  return new Date(`1970-01-01T${first}`) > new Date(`1970-01-01T${second}`) ? first : second
}

/**
 * Will return the early hour time between the parameters
 * @param first contains the hour time such as: "HH:MM:SS"
 * @param second contains the hour time such as: "HH:MM:SS"
 * @returns either first or second parameter
 */
export function minTime(first: string, second: string) {
  if (first === '') {
    return second
  }
  if (second === '') {
    return first
  }
  return new Date(`1970-01-01T${first}`) < new Date(`1970-01-01T${second}`) ? first : second
}

/**
 * Formating hour for the timeSlot field
 * @param input hour time in string like this "HH:MM:SS"
 * @param hour24 (default value is false), determine if the hour must be 12 hour or 24 hour format
 * @param translateI18n optional param: const { t } = useI18n()
 */
export function timeSlotFormatting(
  input?: string,
  hour24: boolean = false,
  translateI18n: ComposerTranslation = useI18n().t,
) {
  if (!input || input.length === 0 || input.split(':').length < 2) {
    return '???'
  }
  const [hour, minute] = input.split(':')
  const nbrHour = parseInt(hour)
  if (hour24) {
    return `${nbrHour}:${minute}`
  } else if (nbrHour >= 12) {
    return translateI18n('{x} pm', { x: `${nbrHour % 12 || 12}:${minute}` })
  }
  return translateI18n('{x} am', { x: `${nbrHour % 12 || 12}:${minute}` })
}

/**
 * Formatting price for the timeSlot field
 * @param currentFunctRoomID current selected FunctRoomID (Venue)
 * @param priceFunctRoomID FunctRoomID of the price
 * @param priceValue Price value (relative to the currentFunctRoom price)
 * @param currencyCode The currencyCode of the price (e.g: "EUR"/"USD"), by default USD is put
 * @param translateI18n optional param: const { t } = useI18n()
 */
export function priceSlotFormatting(
  currentFunctRoomID?: string,
  priceFunctRoomID?: string,
  priceValue?: number,
  currencyCode: string = 'USD',
) {
  // If this is the current FunctionRoomID (selected)
  if (currentFunctRoomID === priceFunctRoomID && !!priceFunctRoomID) {
    // return `(${translateI18n('Included')})` //! Disable "(Included)" text, from other timeSlot of the venue (Requested by Helene)
    return ''
  }
  // Price hasn't be load again from the API
  if (priceValue === undefined || (currentFunctRoomID === '' && priceFunctRoomID === '')) {
    return null // Loading...
  }
  // Formatted CurrencySymbol with the price (Problem: no dynamic left/right symbol position)
  const priceFormatted = `${getCurrencySign(currencyCode) ?? '$'}${Math.abs(priceValue)}` // || formatPrice(Math.abs(priceValue), currencyCode)
  // Negative difference price
  if (priceValue < 0) {
    return `(-${priceFormatted})`
  }
  // Positive difference price
  if (priceValue > 0) {
    return `(+${priceFormatted})`
  }
  // If price is Zero, display nothing
  return ''
}
